import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  CURATED_FONTS,
  ICuratedFont,
  DEFAULT_FONT_FAMILY,
  TextFormatting,
  buildGoogleFontsUrl,
  fromEditorEvents,
} from '@principle-theorem/editor';
import { EditorMenuItemComponent } from '@principle-theorem/ng-prosemirror';
import {
  DYNAMIC_COMPONENT_DATA,
} from '@principle-theorem/ng-shared';
import { snapshot } from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

@Component({
  selector: 'pt-font-family-menu',
  templateUrl: './font-family-menu.component.html',
  styleUrls: ['./font-family-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FontFamilyMenuComponent extends EditorMenuItemComponent {
  override tooltip = 'Font Family';
  fonts = CURATED_FONTS;
  defaultFont: string = DEFAULT_FONT_FAMILY;
  activeFont$: Observable<string | undefined>;
  isDefaultFontActive$: Observable<boolean>;

  constructor() {
    super();
    const data = inject<{ defaultFont?: string }>(DYNAMIC_COMPONENT_DATA, {
      optional: true,
    });
    if (data?.defaultFont) {
      this.defaultFont = data.defaultFont;
    }
    this._loadCuratedFonts();
    this.activeFont$ = this.editor$.pipe(
      switchMap((editor) =>
        fromEditorEvents(editor).pipe(
          startWith(undefined),
          map(() =>
            editor.state.selection.$head
              .marks()
              .find(
                (mark) => mark.type.name === String(TextFormatting.TextStyle)
              )
          ),
          map((mark) => (mark?.attrs?.fontFamily as string) ?? undefined)
        )
      )
    );
    this.isDefaultFontActive$ = this.activeFont$.pipe(
      map((font) => !font || font === this.defaultFont)
    );
  }

  async selectFont(font: ICuratedFont): Promise<void> {
    const editor = await snapshot(this.editor$);
    editor.chain().focus().setFontFamily(font.fontFamily).run();
  }

  async clearFont(): Promise<void> {
    const editor = await snapshot(this.editor$);
    editor.chain().focus().unsetFontFamily().run();
  }

  private _loadCuratedFonts(): void {
    const doc = inject(DOCUMENT);
    const linkId = 'curated-google-fonts';
    if (!doc.getElementById(linkId)) {
      const link = doc.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = buildGoogleFontsUrl(CURATED_FONTS);
      doc.head.appendChild(link);
    }
  }
}
